import { CheckIcon, LaptopIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { buildContinueUrl } from '@/modules/auth/inAppHandoff';
import { PressAndHoldLink } from '@/pages/signUp/copyLink/PressAndHoldLink';
import { useCopyLink } from '@/pages/signUp/copyLink/useCopyLink';

const SHARE_TEXT = 'Finish signing up for Finlay on your computer.';

/**
 * "Not at your desk? Send yourself the link" under the sign-up buttons on
 * phones, where most paid-ad visitors land but setup (Slack, the first job)
 * happens at a computer. Opens the phone's share sheet (Mail, AirDrop,
 * Slack, Notes...) and copies the link where there is none. Finlay can't
 * send the email itself yet.
 */
export const ContinueOnComputer = () => {
  const posthog = usePostHog();
  const trackEvent = useTrackEvent();
  const [shared, setShared] = useState(false);
  const { state: copyState, copy } = useCopyLink();

  // `shareRefused` tells a browser whose share sheet failed from one that has
  // none, since both end up copying.
  const copyLink = async (url: string, shareRefused?: boolean) => {
    const copied = await copy(url);
    trackEvent('continueOnComputerRequested', {
      method: 'copyLink',
      outcome: copied ? 'copied' : 'failed',
      ...(shareRefused && { shareRefused }),
    });
  };

  const send = async () => {
    const url = buildContinueUrl(
      window.location.href,
      posthog.get_distinct_id(),
    );
    const shareData = { text: SHARE_TEXT, url };
    const canShare =
      'share' in navigator &&
      (typeof navigator.canShare !== 'function' ||
        navigator.canShare(shareData));

    if (!canShare) {
      await copyLink(url);
      return;
    }

    try {
      await navigator.share(shareData);
      setShared(true);
      trackEvent('continueOnComputerRequested', {
        method: 'share',
        outcome: 'shared',
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        trackEvent('continueOnComputerRequested', {
          method: 'share',
          outcome: 'cancelled',
        });
        return;
      }
      // Some in-app browsers expose the share sheet but refuse to open it.
      await copyLink(url, true);
    }
  };

  return (
    // Desktop visitors are already at their computer.
    <div
      aria-live="polite"
      className="text-muted-foreground text-center text-sm lg:hidden"
    >
      {!shared && copyState.status === 'idle' && (
        <p>
          <LaptopIcon
            className="text-muted-foreground mr-1.5 mb-0.5 inline size-4"
            aria-hidden
          />
          Not at your desk?{' '}
          <button
            type="button"
            onClick={send}
            // The padding makes a 44px tap target without moving the text.
            className="text-primary hover:text-primary-700 focus-visible:ring-ring/50 -my-3 inline-block cursor-pointer rounded-sm py-3 font-semibold underline-offset-4 outline-none hover:underline focus-visible:ring-[3px]"
          >
            Send yourself the link
          </button>
        </p>
      )}
      {(shared || copyState.status === 'copied') && (
        <p className="flex items-center justify-center gap-1.5">
          <CheckIcon
            className="text-semantic-560 size-4 shrink-0"
            strokeWidth={3}
            aria-hidden
          />
          {shared
            ? 'Sent. Open it on your computer to finish.'
            : 'Link copied. Open it on your computer to finish.'}
        </p>
      )}
      {copyState.status === 'failed' && (
        <PressAndHoldLink url={copyState.url} />
      )}
    </div>
  );
};
